<template>
  <div>
    <div class="row items-center gutter-x-sm" @keyup.enter="handleSave">
      <q-field
        class="col-12 col-md-2 q-ma-none"
        :error="errors.has('quantity')"
        :error-label="errors.first('quantity')"
      >
        <q-input
          v-model="item.quantity"
          type="number"
          name="quantity"
          v-validate.disable="'required'"
          :float-label="$t('quantity')"
          class="no-shadow"
          color="grey-2"
          inverted-light
        />
      </q-field>

      <q-field
        class="col-12 col-md q-ma-none"
        :error="errors.has('itemName')"
        :error-label="errors.first('itemName')"
      >
        <q-input
          v-model="item.itemName"
          ref="itemName"
          name="itemName"
          type="search"
          autocomplete="off"
          v-validate.disable="'required|min:1|max:150'"
          :float-label="$t('item_name')"
          class="no-shadow"
          color="grey-2"
          inverted-light
          autofocus
          @input="handleItemNameUpdate"
        >
          <infinite-autocomplete
            @fetch="handleInfiniteScroll"
            @search="handleSearch"
            @selected="handleSearchSelect"
            @show="popoverOpen = true"
            @hide="handlePopoverHide"
            :debounce="defaultSearchDebounce"
            :min-characters="0"
            :max-results="200"
            :max-height="'300px'"
          />
        </q-input>
      </q-field>

      <q-field
        class="col-6 col-md-2 q-ma-none responsive-margin"
        :error="errors.has('priceNet')"
        :error-label="errors.first('priceNet')"
      >
        <q-input
          type="number"
          name="priceNet"
          v-validate.disable="'required|max:45'"
          :value="item.price | asDecimal"
          :float-label="$t('price_net')"
          :suffix="currencySymbol"
          class="no-shadow"
          color="grey-2"
          inverted-light
          @input="val => item.price = priceInCents(val, currency)"
        />
      </q-field>

      <q-field
        class="col-6 col-md-2 q-ma-none responsive-margin"
        :error="errors.has('salesTax')"
        :error-label="errors.first('salesTax')"
      >
        <sales-tax-input
          v-model="item.salesTax"
          :float-label="$t('sales_tax')"
          use-default
          required
          class="no-shadow"
          color="grey-2"
          inverted-light
          @show="popoverOpen = true"
          @hide="handlePopoverHide"
        />
      </q-field>
    </div>

    <div class="row gutter-x-sm" @keyup.enter="handleSave">
      <q-field class="col q-ma-none responsive-margin" :error="errors.has('unitName')" :error-label="errors.first('unitName')">
        <q-input
          ref="unitName"
          v-model="item.unitName"
          name="unitName"
          v-validate.disable="'max:45'"
          :float-label="$t('unit_name')"
          class="no-shadow"
          color="grey-2"
          inverted-light
        >
          <q-autocomplete
            :static-data="{ field: 'label', list: defaultUnits }"
            :min-characters="0"
            @show="popoverOpen = true"
            @hide="handlePopoverHide"
          />
        </q-input>
      </q-field>

      <discount-input
        :currency="currency"
        :discount-absolute="item.discountAbsolute"
        :discount-relative="item.discountRelative"
        :type="discountType"
        :float-label="$t('discount')"
        class="col q-ma-none responsive-margin no-shadow"
        color="grey-2"
        inverted-light
        @inputDiscountAbsolute="item.discountAbsolute = $event"
        @inputDiscountRelative="item.discountRelative = $event"
      />
    </div>

    <div class="row">
      <q-field
        :error="errors.has('description')"
        :error-label="errors.first('description')"
        class="col-12 q-ma-none responsive-margin"
      >
        <q-input
          type="textarea"
          v-model="item.description"
          v-validate.disable="'max:2000'"
          name="description"
          rows="2"
          :float-label="$t('description')"
          class="no-shadow"
          color="grey-2"
          inverted-light
        />
      </q-field>
    </div>

    <div class="flex q-mt-lg justify-end">
      <q-btn color="black" class="q-mr-md" flat @click="handleCancel">
        {{ $t('cancel') }}
      </q-btn>

      <q-btn-dropdown
        :label="saveButtonText"
        color="green"
        class="no-shadow"
        split
        @click="handleSave"
      >
        <q-list link>
          <q-item @click.native="handleAddTemplate" v-close-overlay>
            <q-item-main>
              <q-item-tile label>{{ $t('add_item_as_template') }}</q-item-tile>
            </q-item-main>
          </q-item>
        </q-list>
      </q-btn-dropdown>
    </div>
  </div>
</template>

<script>
  import { general, items } from 'src/config'
  import Decimal from 'decimal.js'
  import DiscountInput from 'components/Common/Inputs/DiscountInput.vue'
  import InfiniteAutocomplete from 'components/Common/InfiniteAutocomplete.vue'
  import { ItemsClient } from 'src/api/Items'
  import SalesTaxInput from 'components/Common/Inputs/SalesTaxInput.vue'
  import { createErrorFromResponse } from 'src/utils/error'
  import debounceMixin from 'src/utils/mixins/debounce'
  import { formatMoney } from 'src/utils/format'
  import { getCurrencySymbol } from 'src/utils/money'
  import { isNil } from 'lodash'
  import { snakeCase } from 'change-case-object'

  function emptyItem () {
    return {
      type: 'line-item',
      id: null,
      itemName: null,
      price: null,
      quantity: 1,
      unitName: null,
      salesTax: null,
      description: null,
      itemNumber: null,
      discountAbsolute: null,
      discountRelative: null
    }
  }

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    props: {
      currency: {
        required: true,
        type: String
      },

      activeLineItem: {
        type: Object,
        required: false
      },

      type: {
        required: true,
        validator: (val) => ['invoice', 'quote'].includes(val)
      }
    },

    data () {
      return {
        item: emptyItem(),
        popoverOpen: false,
        isUpdate: false
      }
    },

    computed: {
      discountType () {
        return this.item.discountRelative ? 'rel' : 'abs'
      },

      currencySymbol () {
        return getCurrencySymbol(this.currency)
      },

      salesTaxRates () {
        return general.sales_tax_rates.map(rate => ({ value: rate, label: `${rate} %` }))
      },

      defaultUnits () {
        return items.units.map(unit => ({ value: this.$tc(unit), label: this.$tc(unit) }))
      },

      saveButtonText () {
        const type = this.$tc(this.type, 1)
        return this.isUpdate ? this.$t('update_line_item_button', { type }) : this.$t('add_line_item_button', { type })
      }
    },

    methods: {
      handleSearch (query, done) {
        this.executeSearch(query)
          .then(response => {
            const isLastPage = response.data.meta.pagination.current_page === response.data.meta.pagination.total_pages

            done(this.mapSearchResponse(response.data.data), isLastPage)
          })
      },

      executeSearch (term) {
        const client = new ItemsClient()

        if (!term) {
          return client.sort('item_name:asc')
            .list()
        }

        return client.search(term)
      },

      handleInfiniteScroll (query, index, done) {
        if (query) {
          done([], true)
          return
        }

        new ItemsClient()
          .sort('item_name:asc')
          .page(index + 1)
          .list()
          .then(response => {
            const isLastPage = response.data.meta.pagination.current_page === response.data.meta.pagination.total_pages

            return done(this.mapSearchResponse(response.data.data), isLastPage)
          })
      },

      mapSearchResponse (data) {
        return data.map(item => ({
          value: item,
          icon: 'folder',
          label: item.item_name,
          sublabel: item.item_number,
          stamp: formatMoney(item.price, item.currency) + (item.unit_name ? `/${item.unit_name}` : '')
        }))
      },

      handleSearchSelect (item) {
        item = item.value

        this.item = Object.assign(this.item, {
          type: 'line-item',
          id: item.id,
          itemName: item.item_name,
          price: item.price,
          quantity: item.default_quantity,
          unitName: item.unit_name,
          salesTax: Number(item.sales_tax),
          description: item.description,
          itemNumber: item.item_number,
          discountAbsolute: null,
          discountRelative: null
        })

        this.$validator.reset()
      },

      // We have to delay setting popoverOpen to false, otherwise selecting an item using the enter
      // key would immediately also trigger handleAddItem
      handlePopoverHide () {
        setTimeout(() => {
          this.popoverOpen = false
        }, 250)
      },

      handleItemNameUpdate () {
        // If the user changes the item name and a template item id is connected,
        // remove the id in order to not collect wrong statistics.
        if (this.item.id !== null) {
          this.item.id = null
        }
      },

      focus () {
        this.$refs.itemName.focus()
      },

      priceInCents (value) {
        if (typeof value !== 'number') {
          return
        }

        return new Decimal(value).times(100).round().toNumber()
      },

      handleCancel () {
        this.$emit('cancel')
        this.reset()
      },

      handleSave () {
        this.$validator.validateAll()
          .then(isValid => {
            if (!isValid) {
              return
            }

            const item = { ...this.item }
            this.item = emptyItem()

            if (this.isUpdate) {
              this.$emit('update', item)
            } else {
              this.$emit('add', item)
            }

            this.reset()
          })
      },

      reset () {
        this.item = emptyItem()
        this.isUpdate = false
      },

      handleAddTemplate () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              return
            }

            const data = snakeCase(this.item)

            new ItemsClient()
              .create({
                ...data,
                'default_quantity': data.quantity,
                'currency': general.base_currency
              })
              .then(response => {
                this.$q.notify({ message: this.$t('item_created'), type: 'positive' })
              })
              .catch(createErrorFromResponse)
          })
      }
    },

    watch: {
      activeLineItem: {
        handler (item) {
          if (!isNil(item)) {
            this.item = { ...item }
            this.isUpdate = true
          }
        },
        deep: true,
        immediate: true
      }
    },

    filters: {
      asDecimal (value) {
        if (typeof value !== 'number') {
          return
        }

        return new Decimal(value).dividedBy(100).toFixed(2)
      }
    },

    components: {
      DiscountInput,
      InfiniteAutocomplete,
      SalesTaxInput
    },

    mixins: [
      debounceMixin
    ]
  }
</script>

<style lang="stylus">
  @import '~variables'

  @media (max-width $breakpoint-md-min)
    .invoice-items-list__item-description .responsive-margin
      margin-top: 16px !important
</style>
